// src/main.js

// Phase 86 Round D (2026-05-23) — silence the "ResizeObserver loop completed
// with undelivered notifications" error.
//
// The error is a benign browser warning that fires when a ResizeObserver
// callback triggers a layout change in the same frame as the next observer
// pass. It does NOT indicate a real failure. Per-component rAF-deferred
// callbacks (EmailBlockRenderer / EmailTemplatePreview) reduce frequency,
// but the dev-server overlay catches even a single occurrence and surfaces
// it as a blocking error.
//
// Two-layer fix:
//   1. Patch ResizeObserver globally so every callback runs inside a
//      requestAnimationFrame — eliminates the loop at the source, no matter
//      which component creates the observer (Pinia stores, vendor libs,
//      our own components, anything).
//   2. Capture-phase event listener as a belt-and-suspenders backup that
//      stops the error from reaching the overlay even if some path bypasses
//      the patched ResizeObserver.
//
// Dev-only nuisance; production builds have no overlay so this is harmless.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver
  // Track in-flight rAF tokens per-observer-instance so coalesced callbacks
  // don't queue dozens of frames during a rapid layout cascade.
  const _pending = new WeakSet()
  window.ResizeObserver = class PatchedResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        if (_pending.has(observer)) return
        _pending.add(observer)
        window.requestAnimationFrame(() => {
          _pending.delete(observer)
          try {
            callback(entries, observer)
          } catch (e) {
            /* swallow */
          }
        })
      })
    }
  }
}
const _resizeObserverErrorRegex =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/
window.addEventListener(
  'error',
  e => {
    if (e && typeof e.message === 'string' && _resizeObserverErrorRegex.test(e.message)) {
      e.stopImmediatePropagation()
      e.preventDefault()
    }
  },
  true
)
window.addEventListener(
  'unhandledrejection',
  e => {
    const msg = e?.reason?.message || e?.reason
    if (typeof msg === 'string' && _resizeObserverErrorRegex.test(msg)) {
      e.stopImmediatePropagation()
      e.preventDefault()
    }
  },
  true
)

import '@/styles/tailwind.css'
import '@vuepic/vue-datepicker/dist/main.css'

import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import { firebaseApp, db, useMemoryFirestoreCache } from '@/services/firebaseconfig' // Correctly import Firebase instances
import { waitForPendingWrites } from 'firebase/firestore'
import { releaseFirestore } from '@/services/sessionListeners'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import { useToast } from '@/composables/useToast'

import { useMaintenanceStore } from '@/stores/maintenanceStore'
import { useUserStore } from '@/stores/userStore'
import { checkCacheVersion, setCacheVersion } from '@/utils/cacheVersionCheck'
import {
  AUTH_BOOTSTRAP_TIMEOUT_MS,
  CacheBootstrapTimeoutError,
  activateFirestoreMemoryFallback,
  clearFirestoreIndexedDB,
  countLiveFirestoreClients,
  createCacheBootstrapDeadline,
  createFirestoreCacheResetCoordinator,
  getFirestoreMemoryFallback,
} from '@/utils/clearFirestoreCache'
import {
  PARKED_REASON_CODE,
  clearPendingReset,
  isFailureFallback,
  isPendingResetFor,
  noteBootReset,
  readPendingReset,
  writePendingReset,
} from '@/utils/cacheRefreshPolicy'
import { startBackgroundRefresh } from '@/services/backgroundRefresh'
import '@/composables/useInstallPrompt' // attach the beforeinstallprompt listener early
import { hideInitialSplash, initResumeOverlay } from '@/utils/splashScreen'

// Note: self. ... is deprecated; use service worker for PWA app check.
// window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.VUE_APP_FIREBASE_APPCHECK_DEBUG_TOKEN || false;

import googleMapsPlugin from '@/plugins/googleMaps'
import TippyPlugin from 'vue-tippy'
import 'tippy.js/dist/tippy.css'
import {
  LucideInfo,
  LucideMail,
  LucidePhone,
  LucideUploadCloud,
  LucideTrash2,
  LucideCheckCircle,
  LucideAlertTriangle,
  LucideXCircle,
  LucideLoader,
  LucideChevronDown,
  LucideChevronUp,
} from 'lucide-vue-next'

import HeaderComponent from '@/components/Layout/HeaderComponent.vue'
import FooterComponent from '@/components/Layout/FooterComponent.vue'

import { installConsoleCapture } from '@/composables/useConsoleCapture'
installConsoleCapture()

// ── Console noise control ──
// Set to true to suppress verbose debug logs in development
const SUPPRESS_DEBUG_LOGS = false

if (SUPPRESS_DEBUG_LOGS && process.env.NODE_ENV === 'development') {
  const originalLog = console.log
  const originalInfo = console.info

  // Prefixes to suppress — add/remove as needed
  const SUPPRESSED = [
    '[PERF]',
    '[CommsNotificationStore]',
    '[AlertsIndicator]',
    '[WalkieService]',
    '[WalkieStore]',
    '[GroupChatStore]',
    '[UserChatStore]',
    '[CallStore]',
    '[OrgConfig]',
    '[VoiceCallService]',
    '[App]',
    '📣 Emitting event',
    'Firebase App Initialized',
    'Firebase Analytics',
    '[userStore]',
  ]

  const shouldSuppress = args => {
    const first = args[0]
    if (typeof first !== 'string') return false
    return SUPPRESSED.some(prefix => first.includes(prefix))
  }

  console.log = (...args) => {
    if (!shouldSuppress(args)) originalLog.apply(console, args)
  }
  console.info = (...args) => {
    if (!shouldSuppress(args)) originalInfo.apply(console, args)
  }

  // Suppress console.time/timeEnd [PERF] labels
  const originalTime = console.time
  const originalTimeEnd = console.timeEnd
  console.time = label => {
    if (typeof label === 'string' && label.includes('[PERF]')) return
    originalTime.call(console, label)
  }
  console.timeEnd = label => {
    if (typeof label === 'string' && label.includes('[PERF]')) return
    originalTimeEnd.call(console, label)
  }
}

// Use the mode actually selected during firebaseconfig module evaluation.
// Re-reading the shared reset marker here can race with another tab removing
// it after this tab already initialized memory-only Firestore/Auth.
const startedWithMemoryFirestoreCache = useMemoryFirestoreCache
if (startedWithMemoryFirestoreCache && !getFirestoreMemoryFallback()) {
  // Booted memory-only because a peer's reset marker was set at module evaluation (or the
  // marker vanished in between): this tab holds no database connection and returns to the
  // persistent cache on its own once no reset is pending (backgroundRefresh 'restore').
  activateFirestoreMemoryFallback(
    PARKED_REASON_CODE,
    'Parked while another tab rebuilds the shared offline cache; this tab returns to the persistent cache on its own.'
  )
}
// One lock, one delete boundary (C-09). Shared with the background refresher after mount.
const cacheResetCoordinator = createFirestoreCacheResetCoordinator({
  drainPendingWrites: () => waitForPendingWrites(db),
  // Closes every tracked listener first, so none reports "Firestore shutting down" (sessionListeners.js).
  terminateFirestore: () => releaseFirestore(db),
  setCacheVersion,
  clearIndexedDB: () =>
    clearFirestoreIndexedDB({ projectId: firebaseApp.options.projectId || 'gse-main-site' }),
  // Liveness comes from the SDK's own clientMetadata records (4 s heartbeat), never from the
  // localStorage client keys, which an idle live tab stops rewriting.
  countLiveClients: () =>
    countLiveFirestoreClients({ projectId: firebaseApp.options.projectId || 'gse-main-site' }),
  isStillPending: version => isPendingResetFor(undefined, version),
  clearPendingReset: () => clearPendingReset(),
})

function reloadWithMemoryFirestoreCache(reasonCode, message) {
  activateFirestoreMemoryFallback(reasonCode, message)
  window.location.reload()
}

// Wrap bootstrap in an async function to control initialization order.
async function bootstrap() {
  const bootstrapDeadline = createCacheBootstrapDeadline()
  // Phase 43: Check cache version BEFORE any Firestore queries fire.
  // If the remote version is ahead of localStorage, clear IndexedDB so the
  // SDK re-fetches fresh data instead of serving stale cached documents.
  let cacheVersionResult = null
  try {
    cacheVersionResult = await bootstrapDeadline.run(checkCacheVersion())
  } catch (error) {
    if (error instanceof CacheBootstrapTimeoutError) {
      // The version check is localStorage + a network read — a timeout here
      // signals a slow connection, not a wedged IndexedDB. Treat it exactly
      // like the fetch-failure path below and proceed normally: dropping the
      // tab into network-only recovery mode punished every slow mobile boot
      // with the "close other tabs" banner. Worst case of proceeding is one
      // session on yesterday's cache; the next healthy boot re-checks.
      console.warn('[App] Cache version check timed out; proceeding without blocking startup.')
    } else {
      console.warn('[App] Cache version check failed, proceeding normally:', error)
    }
  }

  if (cacheVersionResult?.needsClear) {
    const { remoteVersion, localVersion, forceReload, reason } = cacheVersionResult
    // Acknowledge on first sight. Until 2026-09-05 the local version advanced only after a
    // successful single-tab delete, so every boot until then saw local < remote again: the
    // recovery banner on every open. Seeing a version and rebuilding the
    // cache are two different facts; the second lives in the shared pending-reset marker.
    writePendingReset(undefined, { version: remoteVersion, forceReload, reason })
    setCacheVersion(remoteVersion)
    console.log(
      `[App] Cache version ${localVersion} -> ${remoteVersion} acknowledged${reason ? ` (${reason})` : ''}; cache rebuild pending.`
    )
  } else if (cacheVersionResult?.status === 'error') {
    console.warn('[App] Cache version check failed, proceeding normally:', cacheVersionResult.error)
  }

  const pendingReset = readPendingReset()
  if (pendingReset && !startedWithMemoryFirestoreCache && !noteBootReset()) {
    // Loop insurance: at most two boot-time rebuild attempts per five minutes per tab. Past that
    // the tab boots normally and the background refresher takes over when it is quiet.
    console.warn('[App] Cache rebuild attempted twice already in this tab; booting normally.')
  } else if (pendingReset && !startedWithMemoryFirestoreCache) {
    // Silent rebuild when this is provably the only client (one tab, most phones): drain,
    // terminate, delete, reload. About two seconds, no UI. With peers holding the database the
    // tab boots normally and the background refresher finishes the job when tabs go quiet.
    try {
      const resetResult = await bootstrapDeadline.run(
        cacheResetCoordinator.reset({
          remoteVersion: pendingReset.version,
          ownsPersistentClient: true,
        })
      )
      if (['cleared', 'already-current'].includes(resetResult.status)) {
        console.log(
          `[App] Persistent cache rebuilt for version ${pendingReset.version}; reloading.`
        )
        window.location.reload()
        return
      }
      if (resetResult.terminated) {
        // Firestore is gone in this tab and the delete did not happen: park (memory-only) so
        // the next boot tries again from a clean instance. No banner.
        reloadWithMemoryFirestoreCache(
          PARKED_REASON_CODE,
          'Parked after an interrupted cache rebuild.'
        )
        return
      }
      if (pendingReset.forceReload) {
        // The operator said pre-bump document shapes must not render. Peers hold the database,
        // so this tab runs memory-only (network reads, no banner) until the rebuild completes.
        reloadWithMemoryFirestoreCache(
          PARKED_REASON_CODE,
          'Parked: a forced cache rebuild is pending while other tabs hold the shared cache.'
        )
        return
      }
      console.log(
        `[App] Cache rebuild deferred (${resetResult.status}); other tabs hold the shared cache. Continuing on the persistent cache.`
      )
    } catch (error) {
      // A timeout may have fired after terminate; the coordinator issues no delete once aborted,
      // but this tab's Firestore may be gone. Park (memory-only) and try again at the next boot.
      cacheResetCoordinator.abort()
      console.warn('[App] Cache rebuild did not finish; parking this tab:', error?.message || error)
      reloadWithMemoryFirestoreCache(
        PARKED_REASON_CODE,
        error instanceof CacheBootstrapTimeoutError
          ? 'Parked: the cache rebuild did not finish within the boot budget.'
          : 'Parked: the cache rebuild failed.'
      )
      return
    }
  }

  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)

  // Initialize the user store first.
  const userStore = useUserStore()

  // Start the auth listener and AWAIT its initial resolution.
  // This ensures getRedirectResult() runs and completes before any other store or the router.
  const authInitialization = (async () => {
    await userStore.initAuthListener()
    await userStore.waitForInitialization()
  })()
  try {
    // Fresh, longer deadline for auth: it includes getRedirectResult() network
    // round-trips, and inheriting whatever the 8s cache deadline had left
    // routinely tripped "Startup recovery mode" on mobile connections. A real
    // IndexedDB wedge still gets caught — just with a budget sized for auth.
    const authDeadline = createCacheBootstrapDeadline({ timeoutMs: AUTH_BOOTSTRAP_TIMEOUT_MS })
    await authDeadline.run(authInitialization)
  } catch (error) {
    if (error instanceof CacheBootstrapTimeoutError) {
      if (!startedWithMemoryFirestoreCache) {
        reloadWithMemoryFirestoreCache(
          'auth-bootstrap-timeout',
          'Authentication storage took too long; this tab restarted without the persistent cache.'
        )
        return
      }
      activateFirestoreMemoryFallback(
        'auth-bootstrap-timeout',
        'Authentication storage is still slow; this tab keeps reading from the network.'
      )
      console.warn('[App] Authentication initialization timed out; continuing memory-only.')
    } else {
      console.warn('[App] Authentication initialization failed; continuing:', error)
    }
  }

  // Now that auth is resolved, it's safe to initialize other stores.
  const maintenanceStore = useMaintenanceStore()
  maintenanceStore.start()

  // Continue with the rest of the app setup
  app.use(googleMapsPlugin, {
    apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY_2,
    libraries: ['places', 'drawing', 'geometry'],
  })
  app.use(Toast, {
    position: 'top-right',
    timeout: 4000,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    pauseOnHover: true,
    draggable: false,
    showCloseButtonOnHover: true,
    closeButton: false,
    icon: true,
    maxToasts: 5,
    newestOnTop: true,
    transition: 'Vue-Toastification__fade',
    toastDefaults: {
      success: { timeout: 3000 },
      error: { timeout: 8000 },
      warning: { timeout: 5000 },
      info: { timeout: 4000 },
    },
  })
  // Initialize the unified toast composable (registers eventBus listener)
  useToast()
  app.use(TippyPlugin, {
    directive: 'tippy',
    component: 'tippy',
    defaultProps: {
      theme: 'tailwind',
      animation: 'shift-away',
      arrow: true,
      duration: [200, 150],
    },
  })
  // Global components
  app.component('HeaderComponent', HeaderComponent)
  app.component('FooterComponent', FooterComponent)
  app.component('LucideInfo', LucideInfo)
  app.component('LucideMail', LucideMail)
  app.component('LucidePhone', LucidePhone)
  app.component('LucideUploadCloud', LucideUploadCloud)
  app.component('LucideTrash2', LucideTrash2)
  app.component('LucideCheckCircle', LucideCheckCircle)
  app.component('LucideAlertTriangle', LucideAlertTriangle)
  app.component('LucideXCircle', LucideXCircle)
  app.component('LucideLoader', LucideLoader)
  app.component('LucideChevronDown', LucideChevronDown)
  app.component('LucideChevronUp', LucideChevronUp)

  // Now that auth is initialized, the router can safely run its guards.
  app.use(router)
  app.mount('#app')

  // Clear the cold-start splash now that the app is mounted; arm the
  // wake-from-suspend overlay (installed PWA / phones only).
  try {
    hideInitialSplash()
    initResumeOverlay()
  } catch (e) {
    console.warn('[splash]', e)
  }

  // Tabs pick up cache-version bumps themselves: park when peers hold the shared cache, rebuild
  // when alone, reload when hidden, idle, or at a route change. Never during a call or an edit.
  try {
    startBackgroundRefresh({ coordinator: cacheResetCoordinator })
  } catch (e) {
    console.warn('[backgroundRefresh] failed to start', e)
  }
  // The only user-facing trace of memory-only mode: one line, once, for FAILURE reasons (auth
  // storage timeout, IndexedDB corruption). Parked tabs say nothing and return on their own.
  const memoryFallback = getFirestoreMemoryFallback()
  if (isFailureFallback(memoryFallback)) {
    console.warn(`[App] Memory-only Firestore for this tab (${memoryFallback.reasonCode}).`)
    try {
      useToast().info(
        'Offline cache is off in this tab while the browser recovers. Everything still works; data loads from the network.'
      )
    } catch (e) {
      console.warn('[App] toast unavailable', e)
    }
  }

  // PWA service worker (production only). Minimal / no-cache — see
  // public/service-worker.js. Registered on load so it never competes with
  // initial render, and so deploys are never served stale.
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .catch(e => console.warn('[PWA] Service worker registration failed:', e))
    })
  }

  console.log('Firebase App Initialized and Vue App Mounted:', firebaseApp)
}

bootstrap()
